import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import isEqual from 'lodash-es/isEqual'
import {useMemo} from 'react'

import type {DraftIssue} from '../content-preview-types'
import {useDraftIssueFormSync} from './use-draft-issue-form-sync'
import {useOnChangeCallback} from './use-on-change-callback'

const DEFAULT_FIELDS: IssueFieldWithUnsavedValue[] = []

export function useDraftIssueIssueFields(draftIssue: DraftIssue) {
  const {issueFields: formIssueFields, setIssueFields: setFormIssueFields} = useIssueCreateMetadata()
  const onChange = useOnChangeCallback(draftIssue)

  // The model does not produce issue fields, so the "resolved" value is whatever
  // is already on the draft (or empty). This still drives the form-sync lifecycle
  // for switching between drafts.
  const resolverResult = useMemo(
    () => ({isLoading: false, data: draftIssue.issueFields ?? DEFAULT_FIELDS}),
    [draftIssue.issueFields],
  )

  return useDraftIssueFormSync(draftIssue, {
    draftValue: draftIssue.issueFields,
    formValue: formIssueFields,
    setFormValue: setFormIssueFields,
    onChange: (newValue, skipVersionBump) => onChange({issueFields: newValue}, skipVersionBump),
    resolverResult,
    hasPermission: repo => repo?.viewerIssueCreationPermissions?.canSetIssueFields,
    isEqual,
    defaultValue: DEFAULT_FIELDS,
  })
}
